import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LoadingSpinner from './LoadingSpinner';

import { useDispatch, useSelector } from 'react-redux';
import { fetchPrompts, updatePrompt, sendMessage } from '../services/apiService';
import {
  setSelectedTileId,
  setIsActiveChat,
} from '../reducers/uiReducer';
import { setUserPrompts } from '../reducers/promptsReducer';
import { addMessage, selectChatContext } from '../reducers/messagesReducer';
import { setLoading } from '../reducers/loadingReducer';
import { showToast } from '../reducers/uiReducer';
import getPromptIcon from './PromptIcon';

const PromptTable = ({
  setPromptCallBack,
  onSelectPrompt,
  onSelectNewPrompt,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userPrompts = useSelector((state) => state.prompts.userPrompts);
  const chatContext = useSelector(selectChatContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchUserPrompts = async () => {
    try {
      setIsLoading(true);
      const prompts = await fetchPrompts();
      setFilteredPrompts(prompts.data || []);
      dispatch(setUserPrompts(prompts.data || []));
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunPrompt = async (text, prompt) => {

    setPromptCallBack(text, prompt);

    // dispatch(setSelectedTileId(null));
    // dispatch(setIsActiveChat(true));
    // dispatch(setLoading(true));

    // const newUserMessage = {
    //   role: 'user',
    //   componentType: 'text',
    //   content: [{ type: 'text', text: prompt.promptText }],
    // };

    // dispatch(addMessage(newUserMessage));

    // try {
    //   const response = await sendMessage([...chatContext, newUserMessage]);
    //   const reply = response?.choices?.[0]?.message?.content;
    //   if (reply) {
    //     dispatch(addMessage({
    //       role: 'assistant',
    //       content: [{ type: 'text', text: reply }],
    //     }));
    //   }
    // } catch (error) {
    //   console.error('Failed to send message:', error);
    //   dispatch(addMessage({
    //     role: 'error',
    //     content: [{ type: 'text', text: 'Service Unavailable.' }],
    //   }));
    //   dispatch(showToast({ message: 'Failed to send message', severity: 'error' }));
    // } finally {
    //   dispatch(setLoading(false));
    // }
  };

  useEffect(() => {
    fetchUserPrompts();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      const filtered = Array.isArray(userPrompts)
        ? userPrompts.filter(p =>
          p?.title?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
      setFilteredPrompts(filtered);
    }, 200);
    return () => clearTimeout(delay);
  }, [searchTerm, userPrompts]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
        />
        <Tooltip title="Add New Prompt">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => {
              onSelectPrompt({ promptText: '', title: '', description: '' });
              onSelectNewPrompt();
            }}
            disabled={isLoading}
          >
            Prompt
          </Button>
        </Tooltip>
      </Box>

      <TableContainer
        sx={{
          maxHeight: 450,
          minHeight: 200,
          overflowY: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 10,
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <LoadingSpinner size={30} />
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}> </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Prompt</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrompts?.length > 0 ? (
                filteredPrompts.map((prompt, index) => (
                  <TableRow
                    key={prompt.id || index}
                    hover
                    onClick={() => onSelectPrompt(prompt)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? theme.palette.grey[900]
                        : 'rgb(255, 255, 255)',
                      '&:hover': {
                        backgroundColor: theme.palette.grey[900],
                      },
                    }}
                  >
                    <TableCell>{getPromptIcon(prompt.type)}</TableCell>
                    <TableCell>{prompt.title}</TableCell>
                    <TableCell>{prompt.description}</TableCell>
                    <TableCell>
                      <Tooltip title="Run">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRunPrompt(prompt.description, prompt);
                          }}
                        >
                          <PlayArrowIcon sx={{ fontSize: 30 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No prompts found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default PromptTable;
